<template>
  <div class="bg-cw-green-light h-[100lvh] w-full relative">
    <div class="absolute inset-0 bg-cw-dark-alt/80 z-0"></div>

    <!-- Additional Points Screen (shown first if there are additional points) -->
    <div v-if="showAdditionalPointsScreen" class="flex justify-center items-center min-h-screen">
      <div class="flex gap-8 items-end">
        <section class="flex flex-col z-10 justify-center heightSm:ml-[10%] heightMd:ml-[15%] md:ml-[16%]">
          <h2 class="heightMd:text-2xl text-4xl text-center mb-12 heightMd:mb-6 text-cw-light font-bold">Great game 🎉</h2>
          <div
            class="bg-cw-dark heightSm:min-w-[300px] heightMd:min-w-[400px] md:min-w-[460px] xl:min-w-[550px] relative rounded-3xl flex flex-col gap-4 heightMd:gap-2 py-10 px-9 heightMd:px-7 heightMd:py-6 heightMd:max-h-[260px] overflow-y-auto"
            ref="scrollContainer"
          >
            <div
              v-for="(player, index) in sortedPlayersWithAdditional"
              :key="player.id"
              class="relative bg-[#2D2720] rounded-2xl"
              :ref="el => setItemRef(el, index)"
              :class="[{ 'heightMd:opacity-50': !isItemFullyVisible[index] }, { 'border-white border-2': player.id === user?.id && isKiddiesAccount }]"
            >
              <div class="flex justify-between px-4" :class="user?.id === player.id ? ' pt-2' : 'py-2 '">
                <div class="flex heightMd:gap-3 gap-5 items-center">
                  <BaseAvatar class="heightMd:w-16 heightMd:h-16 w-24 h-24" :src="player.avatarUrl" :size="avatarSize" />
                  <div class="flex flex-col">
                    <p class="text-white font-bold text-lg heightMd:text-xs">
                      {{ player.username }}
                      {{ user?.id === player.id ? '(You)' : '' }}
                      <span class="font-normal">{{ player.status === 'exited' ? ' left the game' : '' }}</span>
                    </p>
                  </div>
                </div>
                <div class="flex items-center heightMd:gap-3 gap-4">
                  <div class="flex items-center gap-2">
                    <span v-if="!isKiddiesAccount" class="text-[#AA9A88] font-bold heightMd:text-xs text-[16px] xl:text-lg">{{ player.points }} {{ getPlayerRankIcon(index) ? 'pts' : 'points' }}</span>
                    <span v-if="isKiddiesAccount" class="text-[#AA9A88] font-bold heightMd:text-xs text-[16px] xl:text-lg">{{ player.points }} {{ player.status == 'exited' ? 'points' : 'pts' }}</span>

                    <!-- Additional Points Display -->
                    <span v-if="player.additionalPoints" class="text-yellow-400 font-bold heightMd:text-xs text-[16px] xl:text-lg">+{{ player.additionalPoints.points }}pt</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="flex flex-col gap-1 z-10">
          <div v-for="player in sortedPlayersWithAdditional" :key="player.id">
            <div
              v-if="player.additionalPoints"
              class="bg-cw-dark-alt heightSm:rounded-lg rounded-xl md:min-w-[225px] heightSm:min-w-[150px] heightMd:min-w-[190px] heightSm:py-3 heightSm:px-2.5 heightMd:py-3 heightMd:px-4 py-4.5 px-6"
            >
              <p class="text-cw-grey-2 heightMd:text-xs heightSm:text-[10px] text-sm xl:text-[14px] tracking-normal">
                {{ player.username }} played
                <span class="text-white font-bold heightMd:text-xs heightSm:text-[10px] text-sm xl:text-[14px]">{{ player.additionalPoints.word }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Final Game Ended Screen -->
    <div v-else class="grid grid-cols-[2fr_3fr] gap-24 heightSm:gap-12">
      <section class="lg:mt-[65%] xl:mt-[42%] md:mt-[45%] heightMd:mt-[50%] flex flex-col items-center gap-8 heightMd:gap-6 z-10 md:ml-[120px] heightSm:ml-20 ml-10 lg:ml-[160px] xl:ml-[180px]">
        <BaseButton class="md:px-10 md:py-2.5 lg:px-12 lg:py-3.5" background-color="var(--color-cw-green)" border-color="#151E1A" @click="playAgain" v-if="isBot">
          <p class="w-full !text-sm md:!text-base xl:!text-xl">Play again</p>
        </BaseButton>
        <BaseButton class="md:px-10 md:py-2.5 lg:px-12 lg:py-3.5" background-color="var(--color-cw-olive)" border-color="var(--color-cw-olive-border-shadow)" @click="resetGame">
          <p class="w-full !text-sm md:!text-base xl:!text-xl">Go home</p>
        </BaseButton>
      </section>
      <section class="mt-[20%] flex flex-col md:mt-[12%] heightMd:mt-[8%] lg:mr-30 xl:mr-40 mr-10 heightSm:mr-5 z-10">
        <h2 class="heightMd:text-2xl text-4xl text-center mb-12 heightMd:mb-6 text-cw-light font-bold">Great game 🎉</h2>
        <div class="bg-cw-dark relative rounded-3xl flex flex-col gap-4 heightMd:gap-2 py-10 px-9 heightMd:px-7 heightMd:py-6 heightMd:max-h-[260px] overflow-y-auto" ref="scrollContainer">
          <div
            v-for="(player, index) in finalSortedPlayers"
            :key="player.id"
            class="relative bg-[#2D2720] rounded-2xl"
            :ref="el => setItemRef(el, index)"
            :class="[{ 'heightMd:opacity-50': !isItemFullyVisible[index] }, { 'border-white border-2 ': player.id === user?.id && isKiddiesAccount }]"
          >
            <div class="flex justify-between px-4" :class="user?.id === player.id ? ' pt-2' : 'py-2 '">
              <div class="flex heightMd:gap-3 gap-5 items-center">
                <BaseAvatar class="heightMd:w-16 heightMd:h-16 w-24 h-24" :src="player.avatarUrl" :size="avatarSize" />
                <p class="text-white font-bold text-lg heightMd:text-xs">{{ player.username }} {{ user?.id === player.id ? '(You)' : '' }} {{ player.status === 'exited' ? ' left the game' : '' }}</p>
              </div>
              <div class="flex items-center heightMd:gap-3 gap-4">
                <span v-if="!isKiddiesAccount" class="text-[#AA9A88] font-bold heightMd:text-xs text-[16px] xl:text-lg">{{ player.points }} {{ getPlayerRankIcon(index) ? 'pts' : 'points' }}</span>
                <span v-if="isKiddiesAccount" class="text-[#AA9A88] font-bold heightMd:text-xs text-[16px] xl:text-lg">{{ player.points }} {{ player.status == 'exited' ? 'points' : 'pts' }}</span>
                <component v-if="getPlayerRankIcon(index) && !isKiddiesAccount" :is="getPlayerRankIcon(index)" class="heightMd:w-8 heightMd:h-8 w-16 h-16" />
                <span v-else-if="isKiddiesAccount && index === 0" class="text-white font-bold text-lg heightMd:text-xs">
                  <WinnerIcon class="lg:h-20 lg:w-20" />
                </span>
              </div>
            </div>
            <div
              v-if="user?.id === player.id && isKiddiesAccount && player.points > 0"
              class="px-3.5 py-0.5 bg-cw-green bottom-0 left-0 rounded-b-2xl flex items-center text-[10px] md:text-xs font-medium tracking-normal justify-center"
            >
              +{{ player.points }}XP
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useGameStore } from '@/stores/game'
import { computed, ref, reactive, onMounted, onUnmounted, useTemplateRef } from 'vue'
import GoldBadge from '@/assets/svgs/others/gold.svg'
import SilverBadge from '@/assets/svgs/others/silver.svg'
import BronzeBadge from '@/assets/svgs/others/bronze.svg'
import useSocket from '@/composables/useSocket'
import { AppWindowEvent, GameEvent } from '@/types/Events'
import { useHelpers } from '@/composables/useHelpers'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import WinnerIcon from '@/assets/svgs/others/winner.svg'
import type { PlayerT } from '@/types/User'
import { useSound } from '@/composables/useSound'

const { gameState, resetGame } = useGameStore()
const { isKiddiesAccount, user } = storeToRefs(useUserStore())
const socket = useSocket()
const { handleCustomError, sendParentWindowMessage } = useHelpers()
const { playGameWon } = useSound()

const windowHeight = ref(window.innerHeight)
const showAdditionalPointsScreen = ref(false)
const playersWithFinalPoints = ref<PlayerT[]>([])
const autoAdvanceTimer = ref<ReturnType<typeof setTimeout> | null>(null)

const scrollContainer = useTemplateRef('scrollContainer')
const itemRefs = ref<(HTMLElement | null)[]>([])
const isItemFullyVisible = reactive<Record<number, boolean>>({})

onMounted(() => {
  window.addEventListener('resize', updateWindowHeight)
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', checkVisibility)
    checkVisibility()
  }

  if (isUserWinner.value) {
    playGameWon()
  }

  checkForAdditionalPoints()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateWindowHeight)
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', checkVisibility)
  }

  if (autoAdvanceTimer.value) {
    clearTimeout(autoAdvanceTimer.value)
  }
})

const updateWindowHeight = () => {
  windowHeight.value = window.innerHeight
}

const avatarSize = computed(() => {
  return windowHeight.value >= 640 ? 48 : 24
})

const isBot = computed(() => {
  return gameState?.type == 'singleplayer'
})

const isUserWinner = computed(() => {
  const players = finalSortedPlayers.value.length ? finalSortedPlayers.value : sortedPlayersWithAdditional.value
  return players.length > 0 && players[0].id === user.value?.id
})

const playBot = async () => {
  try {
    await socket.sendEventAsync(GameEvent.PlayBot)
  } catch (error) {
    handleCustomError(error)
  }
}

const playAgain = async () => {
  await playBot()
  sendParentWindowMessage(AppWindowEvent.RestartGame)
}

const getPlayerRankIcon = (index: number) => {
  switch (index) {
    case 0:
      return GoldBadge
    case 1:
      return SilverBadge
    case 2:
      return BronzeBadge
    default:
      return null
  }
}

// Check if there are additional points for any player
const checkForAdditionalPoints = () => {
  const hasAdditional = gameState?.players?.some(player => player.additionalPoints)
  showAdditionalPointsScreen.value = hasAdditional || false

  if (hasAdditional) {
    autoAdvanceTimer.value = setTimeout(() => {
      proceedToFinalScreen()
    }, 8000)
  } else {
    playersWithFinalPoints.value = gameState?.players || []
  }
}

// Sorted players for the additional points screen (with original points)
const sortedPlayersWithAdditional = computed(() => {
  if (!gameState?.players) return []

  return [...gameState.players].sort((a, b) => {
    const aStillInGame = a.status !== 'exited'
    const bStillInGame = b.status !== 'exited'

    if (aStillInGame && !bStillInGame) return -1
    if (!aStillInGame && bStillInGame) return 1

    return b.points - a.points
  })
})

// Final sorted players with additional points added
const finalSortedPlayers = computed(() => {
  if (!playersWithFinalPoints.value.length) return []

  return [...playersWithFinalPoints.value].sort((a, b) => {
    const aStillInGame = a.status !== 'exited'
    const bStillInGame = b.status !== 'exited'

    if (aStillInGame && !bStillInGame) return -1
    if (!aStillInGame && bStillInGame) return 1

    return b.points - a.points
  })
})

// To proceed from additional points screen to final screen
const proceedToFinalScreen = () => {
  if (autoAdvanceTimer.value) {
    clearTimeout(autoAdvanceTimer.value)
    autoAdvanceTimer.value = null
  }

  // Add additional points to players' totals
  const updatedPlayers =
    gameState?.players?.map(player => {
      if (player.additionalPoints) {
        return {
          ...player,
          points: player.points + player.additionalPoints.points
        }
      }
      return player
    }) || []

  playersWithFinalPoints.value = updatedPlayers
  showAdditionalPointsScreen.value = false
}

const setItemRef = (el: any, index: number) => {
  if (el && el instanceof HTMLElement) {
    itemRefs.value[index] = el
  }
}

const checkVisibility = () => {
  if (!scrollContainer.value) return

  const containerRect = scrollContainer.value.getBoundingClientRect()
  const containerTop = containerRect.top
  const containerBottom = containerRect.bottom

  itemRefs.value.forEach((item, index) => {
    if (item) {
      const itemRect = item.getBoundingClientRect()
      const fullyVisible = itemRect.top >= containerTop && itemRect.bottom <= containerBottom
      isItemFullyVisible[index] = fullyVisible
    }
  })
}
</script>
